import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { transformTitle } from 'src/app/helper/class/utilityHelper';

export interface FieldChange {
  key: string;
  label: string;
  oldValue: any;
  newValue: any;
  status: 'added' | 'updated' | 'deleted';
}

export interface ArrayChange {
  name: string;
  label: string;
  addedRows: any[];
  deletedRows: any[];
  updatedRows: { index: number; id: any; changes: FieldChange[] }[];
}

export interface TabChange {
  tabKey: string;
  tabLabel: string;
  fields: FieldChange[];
  arrays: ArrayChange[];
}

@Component({
  selector: 'app-change-preview',
  templateUrl: './change-preview.component.html',
  styleUrls: ['./change-preview.component.scss']
})
export class ChangePreviewComponent implements OnInit, OnChanges {
  @Input() oldData: any = {};
  @Input() newData: any = {};
  @Input() config: any = {}; 

  tabs: TabChange[] = [];
  hasChanges: boolean = false;
  activeTabIndex: number = 0;

  constructor() { }

  ngOnInit(): void {
    this.generateDeepDiff();
  }

  ngOnChanges(changes: SimpleChanges): void {
      if (changes['oldData'] || changes['newData']) {
          this.generateDeepDiff();
      }
  }

  generateDeepDiff() {
    this.tabs = [];
    this.hasChanges = false;

    const newData = this.newData || {};
    const oldData = this.oldData || {};

    // getFormValue() puts BOTH the raw ID (zone: 1) AND the resolved label
    // (zoneName: "North Zone") into the payload. We skip the raw ID key when
    // its label companion is present — avoids showing duplicate / unreadable integers.
    const labelledKeys = new Set<string>();
    Object.keys(newData).forEach(key => {
      const labelKey = key + 'Name';
      if (newData[labelKey] !== undefined && newData[labelKey] !== null) {
        labelledKeys.add(key); // mark raw ID key to be skipped
      }
    });

    const allKeys = Object.keys(newData).filter(key => !labelledKeys.has(key));

    allKeys.forEach(key => {
       const oldObj = oldData[key];
       const newObj = newData[key];

       let tabConfig = this.config?.[key] || {};
       let tabLabel = tabConfig.title || transformTitle(key);

       let tabChange: TabChange = {
           tabKey: key,
           tabLabel: tabLabel,
           fields: [],
           arrays: []
       };

       if (Array.isArray(oldObj) || Array.isArray(newObj)) {
           const arrChange = this.diffArray(key, oldObj || [], newObj || [], tabConfig);
           if (arrChange.addedRows.length || arrChange.deletedRows.length || arrChange.updatedRows.length) {
               tabChange.arrays.push(arrChange);
               this.hasChanges = true;
           }
       } else if (this.isPlainObject(oldObj) || this.isPlainObject(newObj)) {
           const fieldChanges = this.diffObject(oldObj || {}, newObj || {}, tabConfig.fields || {});
           if (fieldChanges.length) {
               tabChange.fields.push(...fieldChanges);
               this.hasChanges = true;
           }
       } else {
           if (this.isChanged(oldObj, newObj)) {
                let status: 'added' | 'updated' | 'deleted' = 'updated';
                if (oldObj === undefined || oldObj === null || oldObj === '') status = 'added';
                else if (newObj === undefined || newObj === null || newObj === '') status = 'deleted';

                tabChange.fields.push({
                    key,
                    label: tabConfig.title || transformTitle(key),
                    oldValue: this.formatValue(oldObj),
                    newValue: this.formatValue(newObj),
                    status
                });
                this.hasChanges = true;
           }
       }

       if (tabChange.fields.length || tabChange.arrays.length) {
            if (!this.isPlainObject(oldObj) && !this.isPlainObject(newObj) && !Array.isArray(oldObj) && !Array.isArray(newObj)) {
                const existingTab = this.tabs.find(t => t.tabKey === 'General');
                if (existingTab) {
                    existingTab.fields.push(...tabChange.fields);
                } else {
                    tabChange.tabKey = 'General';
                    tabChange.tabLabel = 'General Information';
                    this.tabs.push(tabChange);
                }
            } else {
                this.tabs.push(tabChange);
            }
       }
    });
  }

  diffObject(oldObj: any, newObj: any, fieldsConfig: any): FieldChange[] {
       // Only iterate over keys explicitly provided in the new object payload
       // Ignore internal keys like action, id, _tempId from the visual diff
       const keys = Object.keys(newObj || {}).filter(k => k !== 'action' && k !== 'id' && k !== '_tempId');
       const changes: FieldChange[] = [];
       keys.forEach(k => {
          const oVal = oldObj[k];
          const nVal = newObj[k];
          if (this.isChanged(oVal, nVal)) {
              let status: 'added' | 'updated' | 'deleted' = 'updated';
              if (oVal === undefined || oVal === null || oVal === '') status = 'added';
              else if (nVal === undefined || nVal === null || nVal === '') status = 'deleted';
              
              const fConfig = fieldsConfig[k] || {};
              changes.push({
                  key: k,
                  label: fConfig.title || transformTitle(k),
                  oldValue: this.formatValue(oVal, fConfig),
                  newValue: this.formatValue(nVal, fConfig),
                  status
              });
          }
       });
       return changes;
  }

  diffArray(key: string, oldArr: any[], newArr: any[], config: any): ArrayChange {
      const arrChange: ArrayChange = {
          name: key,
          label: config.title || transformTitle(key),
          addedRows: [],
          deletedRows: [],
          updatedRows: []
      };

      // Ensure they are arrays
      oldArr = Array.isArray(oldArr) ? oldArr : [];
      newArr = Array.isArray(newArr) ? newArr : [];

      // If the payload array is completely empty but old array had objects,
      // it means there are no action changes pending (0 additions, 0 updates, 0 deletions).
      // A true deletion in an action-array sends an object with action=3.
      if (newArr.length === 0 && oldArr.length > 0 && typeof oldArr[0] === 'object') {
          return arrChange;
      }

      // Check if this payload uses action flags natively (e.g. action: 'ADD' or 1)
      const isActionPayload = newArr.some(item => item && item.action !== undefined && item.action !== null);

      if (isActionPayload) {
          // Process based on action logs (Partial Array Payload)
          newArr.forEach((item, index) => {
              const action = String(item.action).toUpperCase();
              if (action === 'ADD' || action === '1') {
                  arrChange.addedRows.push(item);
              } else if (action === 'DELETE' || action === 'REMOVE' || action === '3') {
                  const oldItem = oldArr.find(o => o.id === item.id) || item;
                  arrChange.deletedRows.push(oldItem);
              } else if (action === 'UPDATE' || action === 'EDIT' || action === '2') {
                  const oldItem = oldArr.find(o => o.id === item.id || o._tempId === item._tempId);
                  if (oldItem) {
                      const fieldChanges = this.diffObject(oldItem, item, config.fields || {});
                      if (fieldChanges.length) {
                          arrChange.updatedRows.push({ index: index + 1, id: item.id || item._tempId, changes: fieldChanges });
                      }
                  } else {
                      // Fallback if old item not found but it's an update
                      arrChange.addedRows.push(item);
                  }
              }
          });
      } else {
          // Standard full-array mapping
          const oldMap = new Map();
          oldArr.forEach((item, index) => {
              const id = item?.id || item?._tempId || `index_${index}`;
              oldMap.set(id, { item, index });
          });

          const newMap = new Map();
          newArr.forEach((item, index) => {
              const id = item?.id || item?._tempId || `index_${index}`;
              newMap.set(id, { item, index });
          });

          // Find deleted and updated
          oldMap.forEach((oldData, id) => {
              if (!newMap.has(id)) {
                  arrChange.deletedRows.push(oldData.item);
              } else {
                  const newData = newMap.get(id);
                  const fieldChanges = this.diffObject(oldData.item, newData.item, config.fields || {});
                  if (fieldChanges.length > 0) {
                      arrChange.updatedRows.push({
                          index: newData.index + 1,
                          id: id,
                          changes: fieldChanges
                      });
                  }
              }
          });

          // Find added
          newMap.forEach((newData, id) => {
              if (!oldMap.has(id)) {
                  arrChange.addedRows.push(newData.item);
              }
          });
      }

      return arrChange;
  }

  isPlainObject(val: any) {
      return val && typeof val === 'object' && !Array.isArray(val);
  }

  isChanged(oldVal: any, newVal: any): boolean {
    if (oldVal === newVal) return false;
    if (!oldVal && !newVal) return false;
    
    // Ignore internal keys like _tempId
    const cleanOld = this.cleanTempKeys(oldVal);
    const cleanNew = this.cleanTempKeys(newVal);

    if (typeof cleanOld === 'object' || typeof cleanNew === 'object') {
        const changed = JSON.stringify(cleanOld) !== JSON.stringify(cleanNew);
        if (changed) console.log(`[Diff Object] ${JSON.stringify(cleanOld)} !== ${JSON.stringify(cleanNew)}`);
        return changed;
    }
    const changed = String(cleanOld) !== String(cleanNew);
    if (changed) console.log(`[Diff Flat] ${cleanOld} !== ${cleanNew}`);
    return changed;
  }

  private cleanTempKeys(obj: any): any {
      if (!this.isPlainObject(obj)) return obj;
      const newObj = { ...obj };
      delete newObj['_tempId'];
      return newObj;
  }

  formatValue(val: any, config?: any): string {
    if (val === undefined || val === null || val === '') return '-';
    if (config?.type === 'DATE') {
        return val; 
    }
    // Checkbox: value is "Yes"/"No" string (or legacy 1/0)
    if (config?.type === 'BOOLEAN' || config?.type === 'checkbox') {
        if (typeof val === 'string' && (val =='1' || val == '0')) {
            return val.toLowerCase() === 'yes' ? 'Yes' : 'No';
        }
        return +val ? 'Yes' : 'No';
    }
    // Dropdown: resolve integer ID → label string using support data in config
    if (config?.selectData && config?.selectKeyName) {
        const pKey = config.selectPrimaryKey || 'id';
        const matched = config.selectData.find(
            (item: any) => String(item[pKey]) === String(val)
        );
        if (matched) {
            return String(matched[config.selectKeyName] ?? val);
        }
    }
    if (this.isPlainObject(val) || Array.isArray(val)) {
        return JSON.stringify(val);
    }
    return String(val);
  }

  formatRowPreview(row: any): string {
      if (!this.isPlainObject(row)) return String(row);
      const parts: string[] = [];
      const keys = Object.keys(row).filter(k => k !== 'id' && k !== '_tempId' && k !== 'action');
      
      // If we have label keys (e.g. zoneName) provided, prefer them but skip the raw ID key
      const labeledKeys = new Set<string>();
      keys.forEach(k => { if(k.endsWith('Name')) labeledKeys.add(k.replace('Name', '')); });
      
      const filteredKeys = keys.filter(k => !labeledKeys.has(k));

      // Show first 3 meaningful fields as a preview
      for (let i = 0; i < Math.min(filteredKeys.length, 3); i++) {
          const key = filteredKeys[i];
          const val = row[key];
          if (val !== undefined && val !== null && val !== '') {
             // Pass undefined as config since we don't have per-field config in row preview loop
             parts.push(`${transformTitle(key)}: ${this.formatValue(val)}`);
          }
      }
      return parts.join(' | ') || 'Empty Row';
  }
}
